/**
 * What an expense was for, as one tap.
 *
 * It used to be a `<select>`, which on a phone is three interactions for a
 * field that is optional: open the wheel, spin it, dismiss it. The list is
 * nine items long and has never been longer — `src/lib/categories.ts` is the
 * one copy of it — so every one of them fits on the screen as its own target,
 * and picking one costs exactly one tap. Pressing the one already chosen puts
 * it back to uncategorised, which is the fourth interaction the wheel used to
 * cost and the reason the line under the row says so while something is
 * picked.
 *
 * Toggle buttons rather than radios, and `aria-pressed` rather than
 * `role="radiogroup"`: there is no "none" option to move to with an arrow key
 * — un-picking is pressing the same button again — and a radio group that
 * cannot be emptied would promise a shape this control does not have. The
 * glyph is decorative; the label beside it is what is read out, because a
 * category identified only by an emoji is a category some people cannot see.
 */

import { CATEGORIES } from '../lib/categories'

export function CategoryPicker({
  value,
  labelledBy,
  onChange,
}: {
  value: string | null
  /** The id of the element naming this group — the row's own label. */
  labelledBy: string
  onChange: (category: string | null) => void
}) {
  return (
    <div className="categories" role="group" aria-labelledby={labelledBy}>
      {CATEGORIES.map((category) => {
        const on = value === category.id
        return (
          <button
            key={category.id}
            type="button"
            aria-pressed={on}
            className={`tab${on ? ' tab--on' : ''}`}
            onClick={() => onChange(on ? null : category.id)}
          >
            <span aria-hidden="true">{category.glyph}</span> {category.label}
          </button>
        )
      })}
    </div>
  )
}
